import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import PageContainer from '../layout/PageContainer';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import type { Product } from '../../models/Product';
import { GET_PRODUCTS_QUERY } from '../../queries/ProductQueries';
import { toUSCurrency } from '../../utilities/CurrencyUtilities';
import { CircularProgress, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { GET_CUSTOMERS_QUERY } from '../../queries/CustomerQueries';
import { arrayToMap } from '../../utilities/MapUtilities';
import { PackagingType } from '../../models/ProductInventoryItem';

const INITIAL_PAGE_SIZE = 100;

export default function ProductList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [getProducts, { loading: getProductsLoading, error: getProductsError, data }] = useLazyQuery(
    GET_PRODUCTS_QUERY);

  const { loading: customersLoading, data: customersData } = useQuery(
    GET_CUSTOMERS_QUERY);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = useState<{
    rows: Product[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [selectedCustomerId, setSelectedCustomerId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      getProducts({
        variables: {
          filter: selectedCustomerId > 0 ? { customerId: { eq: selectedCustomerId } } : {},
        }
      });

      if (getProductsLoading) {
        setIsLoading(true);
      }

      if (getProductsError) {
        setError(getProductsError);
      }

      setRowsState({
        rows: data?.products || [],
        rowCount: data?.products.length || 0,
      });
    } catch (listDataError) {
      setError(listDataError as Error);
    }

    setIsLoading(false);
  }, [getProducts, selectedCustomerId, getProductsLoading, getProductsError, data?.products]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = useCallback(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/dashboard/products/${row.id}`);
    },
    [navigate],
  );

  const handleCreateClick = useCallback(() => {
    navigate('/dashboard/products/new');
  }, [navigate]);

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedCustomerId(
        +event.target.value);

      loadData();
    },
    [loadData],
  );

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 2.5,
        display: 'flex',
      },
      {
        field: 'customerName',
        headerName: 'Customer Name',
        flex: 1.5,
        display: 'flex',
        valueGetter: (_, row) => row.customer?.name || 'N/A'
      },
      {
        field: 'costingType',
        headerName: 'Costing Type',
        flex: 1,
        display: 'flex',
        valueFormatter: (value) => value
      },
      {
        field: 'inventory',
        headerName: 'Inventory',
        sortable: false,
        filterable: false,
        flex: 2,
        display: 'flex',
        renderCell: (params) => {
          const inventoryQuantityOnHand = arrayToMap<PackagingType, number>(
            (params.row.totalQuantityOnHand || []));

          return (
            (!params.row.hasInventory ?
              <Box>
                N/A
              </Box> : (
                <Table aria-label="dense table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} component="th" scope="row" align="left">Type</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} component="th" scope="row" align="right">QOH</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="left">
                        Jars
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="right">
                        {inventoryQuantityOnHand.get(PackagingType.JAR)?.toFixed(0) || 0}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="left">
                        Pouches
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="right">
                        {inventoryQuantityOnHand.get(PackagingType.POUCH)?.toFixed(0) || 0}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="left">
                        Bulk
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="right">
                        {inventoryQuantityOnHand.get(PackagingType.BULK)?.toFixed(2) || 0} lbs
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )
            )
          );
        },
        valueGetter: (_, row) => {
          if (!row.hasInventory) {
            return 'N/A';
          }

          const inventoryQuantityOnHand = arrayToMap<PackagingType, number>(
            (row.totalQuantityOnHand || []));

          return `Bulk: ${inventoryQuantityOnHand.get(PackagingType.BULK)?.toFixed(2) || 0} lbs, Jars: ${inventoryQuantityOnHand.get(PackagingType.JAR) || 0}, Pouches: ${inventoryQuantityOnHand.get(PackagingType.POUCH) || 0}`;
        }
      },
      {
        field: 'totalAverageItemCostPerBatchPound',
        headerName: 'Avg. Item Cost per Batch ($/lb)',
        type: 'number',
        flex: 2,
        display: 'flex',
        valueFormatter: (value) => toUSCurrency(value)
      },
      {
        field: 'totalAverageItemCostPerStandardBatch',
        headerName: 'Avg. Item Cost per Std Batch (50#)',
        type: 'number',
        flex: 2,
        display: 'flex',
        valueFormatter: (value) => toUSCurrency(value)
      }
    ],
    [],
  );

  const pageTitle = 'Products';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="space-between" spacing={2}>
          <Box sx={{ width: 200 }}>
            <TextField
              select
              value={selectedCustomerId}
              onChange={handleTextFieldChange}
              label="Customer"
              size="small"
              name="consumableLineItemUseType"
              fullWidth
            >
              <MenuItem value={0}>All Customers</MenuItem>
              {customersLoading ? (
                <MenuItem disabled>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading...</Typography>
                  </Box>
                </MenuItem>
              ) : (
                (customersData?.customers || [])?.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>{customer.name}</MenuItem>))
              )}
            </TextField>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Reload data" placement="right" enterDelay={1000}>
              <div>
                <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleCreateClick}
              startIcon={<AddIcon />}
            >
              Create
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Box sx={{ flex: 1, width: '100%' }}>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <Box sx={{ height: '64vh', width: '100%' }}>
            <DataGrid
              density="compact"
              rows={rowsState.rows}
              columns={columns}
              getRowHeight={() => 'auto'}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              disableRowSelectionOnClick
              onRowClick={handleRowClick}
              loading={isLoading || getProductsLoading}
              initialState={initialState}
              showToolbar
              pageSizeOptions={[INITIAL_PAGE_SIZE]}
              sx={{
                '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                  outline: 'transparent',
                },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: 'none',
                },
                [`& .${gridClasses.row}:hover`]: {
                  cursor: 'pointer',
                },
              }}
              slotProps={{
                loadingOverlay: {
                  variant: 'circular-progress',
                  noRowsVariant: 'circular-progress',
                },
                baseIconButton: {
                  size: 'small',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}